import {
  archiveThread,
  getThreadDetails,
  getUnreadMessageCount,
  markAllMessagesAsRead,
  markThreadAsPin,
  updateThreadTitle,
} from "components/messaging-thread/service";
import { ThreadDetailsResponse } from "components/messaging-thread/types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  refreshThreadsList,
  selectSelectedThreadId,
  selectThreadDetailsVersion,
  setUnreadMessageCount,
} from "../messagingThreadSlice";

export const useThreadDetails = () => {
  const dispatch = useDispatch();
  const selectedThreadId = useSelector(selectSelectedThreadId);
  const refreshThreadsDetails = useSelector(selectThreadDetailsVersion);

  const [thread, setThread] = useState<ThreadDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);

  const fetchThreadDetails = async (showLoader = true) => {
    if (!selectedThreadId) return;

    if (showLoader) setLoading(true);

    try {
      const data = await getThreadDetails(selectedThreadId);
      await markAllMessagesAsRead(selectedThreadId);
      setThread(data);
      setTitleDraft(data.title);
      setIsEditingTitle(false);
      dispatch(refreshThreadsList());

      const unreadMessageCount = await getUnreadMessageCount();
      dispatch(setUnreadMessageCount(unreadMessageCount.count));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreadDetails(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId]);

  useEffect(() => {
    fetchThreadDetails(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshThreadsDetails]);
  const handleTogglePin = async (thread: ThreadDetailsResponse) => {
    try {
      await markThreadAsPin(thread.id, !thread.isPinned);
      const data = await getThreadDetails(selectedThreadId);
      dispatch(refreshThreadsList());
      setThread(data);
    } catch (error) {
      console.error("Failed to toggle pin", error);
    }
  };

  const handleToggleArchive = async () => {
    if (!thread) {
      return;
    }

    try {
      await archiveThread(thread.id, !thread.isArchived);

      const updatedThread = await getThreadDetails(thread.id);
      setThread(updatedThread);

      dispatch(refreshThreadsList());
    } catch (error) {
      console.error("Failed to archive thread", error);
    }
  };

  const handleSaveTitle = async () => {
    if (!thread || !titleDraft.trim()) return;

    setSavingTitle(true);
    try {
      await updateThreadTitle(thread.id, titleDraft.trim());
      const updatedThread = await getThreadDetails(thread.id);
      setThread(updatedThread);
      setIsEditingTitle(false);
    } finally {
      setSavingTitle(false);
    }
  };

  return {
    thread,
    loading,
    handleTogglePin,
    handleToggleArchive,
    isEditingTitle,
    setIsEditingTitle,
    titleDraft,
    setTitleDraft,
    savingTitle,
    handleSaveTitle,
  };
};
